import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Grid,
  Stack,
  Alert,
  Avatar,
  Divider,
  Switch,
  FormControlLabel,
  Button,
  useTheme
} from '@mui/material';
import {
  Save as SaveIcon,
  Upload as UploadIcon,
  Delete as DeleteIcon,
  Business as BusinessIcon
} from '@mui/icons-material';
import { supabase } from '../../../lib/supabaseClient';
import { useEntity } from '../../../context/EntityContext';
import { LoadingButton, useToast, FormSkeleton } from '../../../components/ui';

interface EntityFormData {
  id: string;
  name: string;
  subdomain: string;
  logoUrl?: string;
  primaryColor: string;
  secondaryColor: string;
  headerBackgroundColor: string;
  headerTextColor: string;
  contactEmail?: string;
  contactPhone?: string;
  address?: string;
  city?: string;
  state?: string;
  zipCode?: string;
  country?: string;
  isActive: boolean;
}

export const EntitySettingsTab: React.FC = () => {
  const theme = useTheme();
  const { showSuccess, showError } = useToast();

  // Use EntityContext for entity data and features
  const {
    entity: contextEntity,
    features,
    isLoading: contextLoading,
    currentUser,
    refreshEntity
  } = useEntity();

  // Local form state for editing
  const [formData, setFormData] = useState<EntityFormData | null>(null);
  const [saving, setSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [error, setError] = useState('');
  const [logoFile, setLogoFile] = useState<File | null>(null);
  const [logoPreview, setLogoPreview] = useState<string>('');

  // Feature flag from EntityContext
  const customThemesEnabled = features.customThemes === true;

  // Initialize form data from context entity
  useEffect(() => {
    if (contextEntity && !formData) {
      initializeFormData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contextEntity]);

  const initializeFormData = async () => {
    if (!contextEntity) return;

    try {
      // Fetch full entity data for settings (includes settings JSONB)
      const { data, error: fetchError } = await supabase
        .from('entities')
        .select('*')
        .eq('id', contextEntity.id)
        .single();

      if (fetchError) throw fetchError;

      const entityFormData: EntityFormData = {
        id: data.id,
        name: data.name,
        subdomain: data.subdomain,
        logoUrl: data.theme?.logoUrl,
        primaryColor: data.theme?.primaryColor || '#532E79',
        secondaryColor: data.theme?.secondaryColor || '#B98330',
        headerBackgroundColor: data.theme?.headerBackgroundColor || '#1976d2',
        headerTextColor: data.theme?.headerTextColor || '#ffffff',
        contactEmail: data.settings?.contactEmail,
        contactPhone: data.settings?.contactPhone,
        address: data.settings?.address,
        city: data.settings?.city,
        state: data.settings?.state,
        zipCode: data.settings?.zipCode,
        country: data.settings?.country || 'USA',
        isActive: data.is_active
      };

      setFormData(entityFormData);
      if (entityFormData.logoUrl) {
        setLogoPreview(entityFormData.logoUrl);
      }
      setError('');
    } catch (err: any) {
      console.error('Error loading entity settings:', err);
      setError(err.message || 'Failed to load entity data');
    }
  };

  const reloadFormData = async () => {
    setFormData(null);
    await refreshEntity();
    await initializeFormData();
  };

  const handleInputChange = (field: keyof EntityFormData, value: any) => {
    if (formData) {
      setFormData({ ...formData, [field]: value });
    }
  };

  const handleLogoChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      // Validate file type
      if (!file.type.startsWith('image/')) {
        setError('Please select an image file');
        return;
      }

      // Validate file size (max 2MB)
      if (file.size > 2 * 1024 * 1024) {
        setError('Logo file size must be less than 2MB');
        return;
      }

      setLogoFile(file);

      // Create preview
      const reader = new FileReader();
      reader.onloadend = () => {
        setLogoPreview(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const uploadLogo = async (): Promise<string | null> => {
    if (!logoFile || !formData) return null;

    try {
      // Create unique file name
      const fileExt = logoFile.name.split('.').pop();
      const fileName = `${formData.id}-${Date.now()}.${fileExt}`;
      const filePath = `logos/${fileName}`;

      // Upload to Supabase Storage
      const { error: uploadError } = await supabase.storage
        .from('entity-assets')
        .upload(filePath, logoFile, {
          cacheControl: '3600',
          upsert: true
        });

      if (uploadError) {
        console.error('Upload error:', uploadError);
        throw uploadError;
      }

      // Get public URL
      const { data: urlData } = supabase.storage
        .from('entity-assets')
        .getPublicUrl(filePath);

      return urlData.publicUrl;
    } catch (err: any) {
      console.error('Error uploading logo:', err);
      setError(err.message || 'Failed to upload logo');
      return null;
    }
  };

  const handleSave = async () => {
    if (!formData) return;

    try {
      setSaving(true);
      setSaveSuccess(false);
      setError('');

      let logoUrl = formData.logoUrl;

      // Upload logo if new file selected
      if (logoFile) {
        const uploadedUrl = await uploadLogo();
        if (uploadedUrl) {
          logoUrl = uploadedUrl;
        } else {
          throw new Error('Failed to upload logo');
        }
      }

      // Update entity
      const updateData = {
        name: formData.name,
        theme: {
          logoUrl: logoUrl,
          primaryColor: formData.primaryColor,
          secondaryColor: formData.secondaryColor,
          headerBackgroundColor: formData.headerBackgroundColor,
          headerTextColor: formData.headerTextColor
        },
        settings: {
          contactEmail: formData.contactEmail,
          contactPhone: formData.contactPhone,
          address: formData.address,
          city: formData.city,
          state: formData.state,
          zipCode: formData.zipCode,
          country: formData.country
        },
        is_active: formData.isActive,
        updated_at: new Date().toISOString()
      };

      console.log('[Settings] Updating entity with:', updateData);
      console.log('[Settings] Logo URL to save:', logoUrl);

      const { data: updateResult, error: updateError } = await supabase
        .from('entities')
        .update(updateData)
        .eq('id', formData.id)
        .select();

      console.log('[Settings] Update result:', updateResult);

      if (updateError) {
        console.error('[Settings] Update error:', updateError);
        throw updateError;
      }

      // Check if update actually affected rows
      if (!updateResult || updateResult.length === 0) {
        throw new Error('Update failed - no rows affected. You may not have permission to update this entity.');
      }

      setSaveSuccess(true);
      showSuccess('Entity settings saved successfully!', 'Settings Updated');
      setLogoFile(null);

      // Reset success state and refresh context
      setTimeout(async () => {
        setSaveSuccess(false);
        await reloadFormData();
      }, 1500);
    } catch (err: any) {
      console.error('Error saving entity:', err);
      showError(err.message || 'Failed to save entity settings');
      setError(err.message || 'Failed to save entity settings');
    } finally {
      setSaving(false);
    }
  };

  const handleRemoveLogo = () => {
    setLogoFile(null);
    setLogoPreview('');
    if (formData) {
      setFormData({ ...formData, logoUrl: undefined });
    }
  };

  // Access check - use currentUser from EntityContext
  if (!currentUser || currentUser.role !== 'admin') {
    return (
      <Alert severity="error">
        Access denied. Admin role required to manage entity settings.
      </Alert>
    );
  }

  // Loading state - check both context loading and form data initialization
  if (contextLoading || !formData) {
    return <FormSkeleton fields={6} />;
  }

  return (
    <Box>
      {/* Header */}
      <Box mb={3}>
        <Typography variant="h5" fontWeight={700} gutterBottom>
          Entity Settings
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Manage your organization's information and branding
        </Typography>
      </Box>

      {/* Alerts - Only show error as success uses toast */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      <Grid container spacing={3}>
        {/* Logo Section */}
        <Grid item xs={12}>
          <Paper elevation={2} sx={{ p: 3 }}>
            <Typography variant="h6" fontWeight={600} gutterBottom>
              Company Logo
            </Typography>
            <Divider sx={{ mb: 3 }} />

            <Stack direction="row" spacing={3} alignItems="center">
              <Avatar
                src={logoPreview}
                sx={{
                  width: 120,
                  height: 120,
                  border: `2px solid ${theme.palette.divider}`
                }}
              >
                <BusinessIcon sx={{ fontSize: 60 }} />
              </Avatar>

              <Stack spacing={2} flex={1}>
                <Typography variant="body2" color="text.secondary">
                  Upload your company logo. Recommended size: 300x300px. Max file size: 2MB.
                </Typography>

                <Stack direction="row" spacing={2}>
                  <Button
                    variant="outlined"
                    component="label"
                    startIcon={<UploadIcon />}
                  >
                    Upload Logo
                    <input
                      type="file"
                      hidden
                      accept="image/*"
                      onChange={handleLogoChange}
                    />
                  </Button>

                  {logoPreview && (
                    <Button
                      variant="outlined"
                      color="error"
                      startIcon={<DeleteIcon />}
                      onClick={handleRemoveLogo}
                    >
                      Remove
                    </Button>
                  )}
                </Stack>
              </Stack>
            </Stack>
          </Paper>
        </Grid>

        {/* Company Information */}
        <Grid item xs={12}>
          <Paper elevation={2} sx={{ p: 3 }}>
            <Typography variant="h6" fontWeight={600} gutterBottom>
              Company Information
            </Typography>
            <Divider sx={{ mb: 3 }} />

            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Company Name"
                  value={formData.name}
                  onChange={(e) => handleInputChange('name', e.target.value)}
                  required
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Subdomain"
                  value={formData.subdomain}
                  disabled
                  helperText="Contact support to change subdomain"
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Contact Email"
                  type="email"
                  value={formData.contactEmail || ''}
                  onChange={(e) => handleInputChange('contactEmail', e.target.value)}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Contact Phone"
                  value={formData.contactPhone || ''}
                  onChange={(e) => handleInputChange('contactPhone', e.target.value)}
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Address"
                  value={formData.address || ''}
                  onChange={(e) => handleInputChange('address', e.target.value)}
                  multiline
                  rows={2}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="City"
                  value={formData.city || ''}
                  onChange={(e) => handleInputChange('city', e.target.value)}
                />
              </Grid>

              <Grid item xs={12} md={3}>
                <TextField
                  fullWidth
                  label="State/Province"
                  value={formData.state || ''}
                  onChange={(e) => handleInputChange('state', e.target.value)}
                />
              </Grid>

              <Grid item xs={12} md={3}>
                <TextField
                  fullWidth
                  label="ZIP/Postal Code"
                  value={formData.zipCode || ''}
                  onChange={(e) => handleInputChange('zipCode', e.target.value)}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Country"
                  value={formData.country || ''}
                  onChange={(e) => handleInputChange('country', e.target.value)}
                />
              </Grid>
            </Grid>
          </Paper>
        </Grid>

        {/* Branding - Only show if custom themes feature is enabled */}
        {customThemesEnabled && (
          <>
            <Grid item xs={12}>
              <Paper elevation={2} sx={{ p: 3 }}>
                <Typography variant="h6" fontWeight={600} gutterBottom>
                  Brand Colors
                </Typography>
                <Divider sx={{ mb: 3 }} />

                <Grid container spacing={3}>
                  <Grid item xs={12} md={6}>
                    <Stack spacing={2}>
                      <Typography variant="body2" color="text.secondary">
                        Primary Color
                      </Typography>
                      <Stack direction="row" spacing={2} alignItems="center">
                        <TextField
                          type="color"
                          value={formData.primaryColor}
                          onChange={(e) => handleInputChange('primaryColor', e.target.value)}
                          sx={{ width: 100 }}
                        />
                        <TextField
                          fullWidth
                          value={formData.primaryColor}
                          onChange={(e) => handleInputChange('primaryColor', e.target.value)}
                          placeholder="#532E79"
                        />
                      </Stack>
                    </Stack>
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <Stack spacing={2}>
                      <Typography variant="body2" color="text.secondary">
                        Secondary Color
                      </Typography>
                      <Stack direction="row" spacing={2} alignItems="center">
                        <TextField
                          type="color"
                          value={formData.secondaryColor}
                          onChange={(e) => handleInputChange('secondaryColor', e.target.value)}
                          sx={{ width: 100 }}
                        />
                        <TextField
                          fullWidth
                          value={formData.secondaryColor}
                          onChange={(e) => handleInputChange('secondaryColor', e.target.value)}
                          placeholder="#B98330"
                        />
                      </Stack>
                    </Stack>
                  </Grid>
                </Grid>
              </Paper>
            </Grid>

            {/* Header Colors */}
            <Grid item xs={12}>
              <Paper elevation={2} sx={{ p: 3 }}>
                <Typography variant="h6" fontWeight={600} gutterBottom>
                  Public Header Colors
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Customize the header appearance on public job pages
                </Typography>
                <Divider sx={{ mb: 3 }} />

                <Grid container spacing={3}>
                  <Grid item xs={12} md={6}>
                    <Stack spacing={2}>
                      <Typography variant="body2" color="text.secondary">
                        Header Background Color
                      </Typography>
                      <Stack direction="row" spacing={2} alignItems="center">
                        <TextField
                          type="color"
                          value={formData.headerBackgroundColor}
                          onChange={(e) => handleInputChange('headerBackgroundColor', e.target.value)}
                          sx={{ width: 100 }}
                        />
                        <TextField
                          fullWidth
                          value={formData.headerBackgroundColor}
                          onChange={(e) => handleInputChange('headerBackgroundColor', e.target.value)}
                          placeholder="#1976d2"
                        />
                      </Stack>
                    </Stack>
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <Stack spacing={2}>
                      <Typography variant="body2" color="text.secondary">
                        Header Text Color
                      </Typography>
                      <Stack direction="row" spacing={2} alignItems="center">
                        <TextField
                          type="color"
                          value={formData.headerTextColor}
                          onChange={(e) => handleInputChange('headerTextColor', e.target.value)}
                          sx={{ width: 100 }}
                        />
                        <TextField
                          fullWidth
                          value={formData.headerTextColor}
                          onChange={(e) => handleInputChange('headerTextColor', e.target.value)}
                          placeholder="#ffffff"
                        />
                      </Stack>
                    </Stack>
                  </Grid>

                  {/* Preview */}
                  <Grid item xs={12}>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                      Preview
                    </Typography>
                    <Box
                      sx={{
                        backgroundColor: formData.headerBackgroundColor,
                        color: formData.headerTextColor,
                        p: 2,
                        borderRadius: 1,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 2
                      }}
                    >
                      {logoPreview && (
                        <img src={logoPreview} alt="Logo" style={{ height: 32 }} />
                      )}
                      <Typography fontWeight={600}>
                        {formData.name || 'Company Name'}
                      </Typography>
                      <Box sx={{ flexGrow: 1 }} />
                      <Typography sx={{ opacity: 0.9 }}>Jobs</Typography>
                      <Typography sx={{ opacity: 0.9 }}>Admin</Typography>
                    </Box>
                  </Grid>
                </Grid>
              </Paper>
            </Grid>
          </>
        )}

        {/* Status */}
        <Grid item xs={12}>
          <Paper elevation={2} sx={{ p: 3 }}>
            <Typography variant="h6" fontWeight={600} gutterBottom>
              Entity Status
            </Typography>
            <Divider sx={{ mb: 3 }} />

            <FormControlLabel
              control={
                <Switch
                  checked={formData.isActive}
                  onChange={(e) => handleInputChange('isActive', e.target.checked)}
                  color="primary"
                />
              }
              label={
                <Box>
                  <Typography fontWeight={500}>
                    {formData.isActive ? 'Entity Active' : 'Entity Disabled'}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {formData.isActive
                      ? 'Entity is active and visible'
                      : 'Entity is disabled - users cannot access'}
                  </Typography>
                </Box>
              }
            />
          </Paper>
        </Grid>

        {/* Save Button */}
        <Grid item xs={12}>
          <Stack direction="row" justifyContent="flex-end" spacing={2}>
            <Button
              variant="outlined"
              onClick={reloadFormData}
              disabled={saving}
            >
              Cancel
            </Button>
            <LoadingButton
              variant="contained"
              size="large"
              loading={saving}
              success={saveSuccess}
              loadingText="Saving..."
              successText="Saved!"
              startIcon={<SaveIcon />}
              onClick={handleSave}
              sx={{ px: 4 }}
            >
              Save Changes
            </LoadingButton>
          </Stack>
        </Grid>
      </Grid>
    </Box>
  );
};
